/**
 * Used for lodash deep path
 * Modified from: https://github.com/Pouja/typescript-deep-path-safe
 * Related article: https://dev.to/pouja/making-lodash-function-get-type-safe-313o
 */
type ResolveType<ObjectType, Path extends string, OrElse> = Path extends keyof ObjectType
  ? ObjectType[Path]
  : Path extends `${infer LeftSide}.${infer RightSide}`
    ? LeftSide extends keyof ObjectType
      ? ResolveType<NoUndefined<ObjectType[LeftSide]>, NoUndefined<RightSide>, OrElse>
      : Path extends `${infer LeftSideArrayProp}[${number}].${infer RightSideArrayProp}`
        ? LeftSideArrayProp extends keyof ObjectType
          ? ObjectType[LeftSideArrayProp] extends Array<infer U>
            ? ResolveType<NoUndefined<U>, NoUndefined<RightSideArrayProp>, OrElse>
            : OrElse
          : OrElse
        : OrElse
    : Path extends `${infer LeftSideArray}[${number}]`
      ? LeftSideArray extends keyof ObjectType
        ? ObjectType[NoUndefined<LeftSideArray>] extends Array<infer U>
          ? U
          : OrElse
        : OrElse
      : OrElse;

/**
 * Gets the value at path of object. If the resolved value is undefined,
 * the defaultValue is returned in its place.
 *
 * @param obj - The object to query
 * @param path - The path of the property to get (supports dot notation and array indices)
 * @param orElse - The value returned for undefined resolved values
 * @returns Returns the resolved value or defaultValue
 *
 * @example
 * const obj = { user: { name: 'John' } };
 * getOr(obj, 'user.name', 'default'); // 'John'
 * getOr(obj, 'user.email', 'no-email'); // 'no-email'
 * getOr(obj, 'items[0]', 'default'); // value at items[0] or 'default'
 */
/**
 * Handles array index access and returns the value or default.
 */
function handleArrayIndexAccess(current: unknown, key: number): { success: true; value: unknown } | { success: false } {
  if (!Array.isArray(current)) {
    return { success: false };
  }
  if (key < 0 || key >= current.length) {
    return { success: false };
  }

  const value = current[key];
  return { success: true, value };
}

/**
 * Handles object property access and returns the value or default.
 */
function handleObjectPropertyAccess(
  current: unknown,
  key: string,
): { success: true; value: unknown } | { success: false } {
  if (typeof current !== 'object' || current === null) {
    return { success: false };
  }
  if (!Object.hasOwn(current, key)) {
    return { success: false };
  }

  const value = (current as Record<string, unknown>)[key];
  return { success: true, value };
}

/**
 * Gets the value at a specific key, handling both array indices and object properties.
 */
function getValueAtKey<OrElse>(
  current: unknown,
  key: string | number,
  isLastKey: boolean,
  orElse: OrElse,
): { success: true; value: unknown; shouldContinue: boolean } | { success: false } {
  if (current === null || current === undefined) {
    return { success: false };
  }

  const accessResult =
    typeof key === 'number' ? handleArrayIndexAccess(current, key) : handleObjectPropertyAccess(current, key);

  if (!accessResult.success) {
    return { success: false };
  }

  const { value } = accessResult;

  if (isLastKey) {
    return {
      success: true,
      value: value === undefined ? orElse : value,
      shouldContinue: false,
    };
  }

  if (value === null || value === undefined) {
    return { success: false };
  }

  return { success: true, value, shouldContinue: true };
}

export function getOr<OrElse, ObjecType, Path extends string>(
  obj: ObjecType | null | undefined,
  path: Path,
  orElse: OrElse,
): ResolveType<ObjecType, Path, OrElse> | OrElse {
  // Handle null/undefined object immediately
  if (obj === null || obj === undefined) {
    return orElse;
  }

  if (path === '') {
    return (obj as Record<string, unknown>)[''] === undefined
      ? orElse
      : ((obj as Record<string, unknown>)[''] as ResolveType<ObjecType, Path, OrElse>);
  }

  const keys = parsePath(path);
  let current: unknown = obj;

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    const isLastKey = i === keys.length - 1;

    const result = getValueAtKey(current, key, isLastKey, orElse);

    if (!result.success) {
      return orElse;
    }

    if (!result.shouldContinue) {
      return result.value as ResolveType<ObjecType, Path, OrElse> | OrElse;
    }

    current = result.value;
  }

  // This should never be reached, but TypeScript needs it
  return current as ResolveType<ObjecType, Path, OrElse> | OrElse;
}

/**
 * Flushes the current string segment to the result array if it's not empty.
 */
function flushCurrentSegment(current: string, result: (string | number)[]): string {
  if (current) {
    result.push(current);
  }
  return '';
}

/**
 * Handles bracket notation parsing (e.g., [0], [123]).
 * Returns the new position and whether parsing should continue.
 */
function handleBracketNotation(
  path: string,
  startIndex: number,
  current: string,
  result: (string | number)[],
): { newCurrent: string; newIndex: number; shouldContinue: boolean } {
  const bracketEnd = path.indexOf(']', startIndex);
  if (bracketEnd === -1) {
    // Malformed path, treat rest as string
    return {
      newCurrent: current + path.slice(startIndex),
      newIndex: path.length,
      shouldContinue: false,
    };
  }

  const indexStr = path.slice(startIndex + 1, bracketEnd);
  const index = Number.parseInt(indexStr, 10);

  if (Number.isNaN(index)) {
    // Not a valid number, treat as string property
    return {
      newCurrent: current + `[${indexStr}]`,
      newIndex: bracketEnd + 1,
      shouldContinue: true,
    };
  }

  result.push(index);
  return {
    newCurrent: '',
    newIndex: bracketEnd + 1,
    shouldContinue: true,
  };
}

/**
 * Handles dot notation parsing.
 */
function handleDotNotation(current: string, result: (string | number)[]): string {
  return flushCurrentSegment(current, result);
}

/**
 * Parses a path string into an array of keys and array indices.
 * Handles dot notation and array bracket notation.
 *
 * @param path - The path string (e.g., 'user.profile.name' or 'items[0].name')
 * @returns Array of string keys and number indices
 *
 * @example
 * parsePath('user.profile.name'); // ['user', 'profile', 'name']
 * parsePath('items[0]'); // ['items', 0]
 * parsePath('users[0].name'); // ['users', 0, 'name']
 */
function parsePath(path: string): (string | number)[] {
  const result: (string | number)[] = [];
  let current = '';
  let i = 0;

  while (i < path.length) {
    const char = path[i];

    if (char === '.') {
      current = handleDotNotation(current, result);
      i++;
    } else if (char === '[') {
      current = flushCurrentSegment(current, result);
      const bracketResult = handleBracketNotation(path, i, current, result);
      current = bracketResult.newCurrent;
      i = bracketResult.newIndex;
      if (!bracketResult.shouldContinue) {
        break;
      }
    } else {
      current += char;
      i++;
    }
  }

  flushCurrentSegment(current, result);

  return result;
}
